import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { MessageService } from 'primeng/api';

import { AgentAdminApiService } from 'src/app/services/agent-admin-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { AgentCustomerProfile, AgentCustomerTier } from 'src/app/shared/agent-admin';

/**
 * API-11 admin tab (Part 2): `AgentCustomerProfiles` upsert for one customer
 * (`cliId`, a `Clients.CLIID`). Not every customer has a profile row yet —
 * `GET .../customer-tier/:cliId` returning `null` is the common case, shown
 * as a blank/default form rather than an error. Save always upserts via
 * `PUT`; `tier_set_by`/`tier_set_at` are never sent — the backend derives
 * both server-side.
 *
 * `account_manager` is a `Person.PERSID` now (not a free-typed username,
 * since the 2026-07-23 identity refactor), picked via `app-personselector`
 * same as the team-members tab.
 *
 * **Read-only on a Legal Entity row (2026-09-20).** This tab is embedded in
 * `edit-client`, bound to whatever `CLIID` that dialog has open (`cliId`) --
 * which may be a customer's Legal Entity, not its Main Company (see
 * `Clients`' `CASTCATID` grouping). Since the backend now resolves tier to
 * the Main Company regardless of which id is sent (see
 * `customerMainCompanyId`'s own doc comment), editing from a Legal Entity's
 * screen would otherwise silently write a *different* company's tier with
 * nothing on screen saying so. `resolveMainCompany()`/`isLegalEntity` detect
 * that case and switch the form to read-only with a link to the Main
 * Company's own `edit-client` instead.
 */
@Component({
  selector: 'app-edit-agent-customer-tier-tab',
  templateUrl: './edit-agent-customer-tier-tab.component.html',
  styleUrl: './edit-agent-customer-tier-tab.component.css',
  encapsulation: ViewEncapsulation.None
})
export class EditAgentCustomerTierTabComponent implements OnChanges, OnDestroy {

  @Input() cliId: number;

  /** Ordered highest discount/priority first, so the hierarchy reads
   *  directly off the dropdown -- see `AgentCustomerTier`'s own doc comment
   *  for what each level means and drives (discount slot, COPILOT mode). */
  tierOptions: { label: string; value: AgentCustomerTier }[] = [
    { label: 'Ключовий партнер (key_partner) — максимальна знижка', value: 'key_partner' },
    { label: 'Ключовий клієнт (key_account) — знижка за домовленістю', value: 'key_account' },
    { label: 'Стандартний (standard)', value: 'standard' },
    { label: 'Новий (new)', value: 'new' },
    { label: 'Крайовий (edge)', value: 'edge' },
  ];

  mainCompanyId: number = null;
  profile: AgentCustomerProfile = null;
  tierSetByName: string = null;
  loaded: boolean = false;
  saving: boolean = false;

  /**
   * The customer conglomerate's Main Company CLIID/name for `cliId`,
   * resolved via `ClientApiService.getMainIdByClientId()` -- the same
   * `ufn_getcompanybylco()` function the backend tier routes already
   * resolve through (`GET /Clients/:id/discount-tier`, `GET`/`PUT
   * /api/agent/admin/customer-tier/:id` -- see
   * `docs/PROMPT_customer-level-discount-auto-set.md`'s "Main Company /
   * Legal Entity" section). When `cliId` is itself the Main Company (the
   * common case -- most `Clients` rows carry no `CASTCATID` at all), this
   * resolves to itself and the form stays fully editable, same as before
   * this existed. When `cliId` is a Legal Entity of a *different* company,
   * `PUT .../customer-tier/:cliId` would silently write that other
   * company's tier, so the form switches to read-only (`isLegalEntity`
   * below) with a link to edit it there instead.
   *
   * NOT the same concept as `mainCompanyId` above (PROXIS/DISTI's own main
   * company, from `GetglobalsettingsService`, used to scope the manager/
   * override pickers below) -- two unrelated "main company" ideas that
   * happen to share a word; kept as separate fields on purpose, per this
   * repo's naming-collision rule (`CLAUDE.md` "Naming").
   */
  customerMainCompanyId: number = null;
  customerMainCompanyName: string = null;
  mainCompanyLoaded: boolean = false;

  private loadQuery: Subscription = null;
  private tierSetByQuery: Subscription = null;
  private saveQuery: Subscription = null;
  private mainCompanyQuery: Subscription = null;

  constructor(
    private agentAdminApi: AgentAdminApiService,
    private cliApi: ClientApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private messageService: MessageService,
    private router: Router,
    private location: Location,
  ) {
  }

  ngOnDestroy(): void {
    if (this.loadQuery) { this.loadQuery.unsubscribe(); }
    if (this.tierSetByQuery) { this.tierSetByQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }
    if (this.mainCompanyQuery) { this.mainCompanyQuery.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.mainCompanyId = this.globalsettingsservice.getMainCompanyId();
    if (changes['cliId'] && this.cliId) {
      this.loadProfile();
      this.resolveMainCompany();
    }
  }

  /**
   * True once `resolveMainCompany()` confirms `cliId` is a Legal Entity of
   * a *different* company. Gates the form to read-only -- see
   * `customerMainCompanyId`'s own doc comment for why. Deliberately false
   * (fully editable) while still resolving, not just while confirmed to be
   * its own Main Company: `mainCompanyLoaded` gates the form separately, so
   * there is never a window where Save is clickable before the resolution
   * completes -- see the template's `[disabled]` bindings.
   */
  get isLegalEntity(): boolean {
    return this.mainCompanyLoaded && this.customerMainCompanyId !== null && this.customerMainCompanyId !== this.cliId;
  }

  resolveMainCompany(): void {
    this.mainCompanyLoaded = false;
    this.customerMainCompanyId = null;
    this.customerMainCompanyName = null;
    this.mainCompanyQuery =
      this.cliApi.getMainIdByClientId(this.cliId).subscribe({
        next: data => {
          const mc = data && data[0];
          this.customerMainCompanyId = mc ? mc.CLIID : this.cliId;
          this.customerMainCompanyName = mc ? (mc.CLIENTSNIKNAME || mc.CLIENTSNAME) : null;
          this.mainCompanyLoaded = true;
        },
        error: err => {
          console.error('*** EditAgentCustomerTierTabComponent resolveMainCompany: Error: ' + err);
          // Fail open to "this is its own Main Company" rather than leave the
          // form stuck disabled forever over a lookup error -- worst case a
          // Legal Entity edit isn't caught this one time, no worse than
          // before this check existed.
          this.customerMainCompanyId = this.cliId;
          this.mainCompanyLoaded = true;
        }
      });
  }

  /** Opens the resolved Main Company's own edit-client form in a new tab --
   *  same HashLocationStrategy-aware idiom as
   *  `CompanyselectorComponent.onEditButton()` (a bare serialized path
   *  404s under this app's hash routing without `Location.prepareExternalUrl()`;
   *  see that method's own comment for the full explanation). */
  onOpenMainCompany(): void {
    if (!this.customerMainCompanyId) { return; }
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-client', this.customerMainCompanyId]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }

  loadProfile(): void {
    this.loaded = false;
    this.tierSetByName = null;
    this.loadQuery =
      this.agentAdminApi.getCustomerTier(this.cliId).subscribe({
        next: profile => {
          // No profile row yet is the common case (Phase 7-A seed only covers
          // key-accounts/overrides) -- show a blank form, not an error.
          this.profile = profile || { customer_id: this.cliId, tier: 'standard', account_manager: null, override_company_id: null, notes: '' };
          this.loaded = true;
          if (this.profile.tier_set_by) { this.loadTierSetByName(this.profile.tier_set_by); }
        },
        error: err => {
          console.error('*** EditAgentCustomerTierTabComponent loadProfile: Error: ' + err);
          this.messageService.add(err?.status === 403
            ? { severity: 'error', summary: 'Недостаточно прав', detail: 'Требуется роль portal-admin для управления рівнем клієнта' }
            : { severity: 'error', summary: 'Не вдалося завантажити рівень клієнта', detail: err?.message || 'Unknown error' });
        }
      });
  }

  /** Resolves `tier_set_by` (a `Person.PERSID`) to a display name for the
   *  "Востаннє змінено" line — falls back to the raw ID if the lookup fails. */
  loadTierSetByName(persId: number): void {
    this.tierSetByQuery =
      this.cliApi.getPerListEntry(persId).pipe(
        filter(data => data && data.length > 0),
        map(data => data[0]),
      ).subscribe({
        next: person => { this.tierSetByName = person.personFullName || person.personSaveAs || String(persId); },
        error: err => {
          console.error('*** EditAgentCustomerTierTabComponent loadTierSetByName: Error: ' + err);
          // Non-fatal: falls back to showing the raw PERSID in the template.
        }
      });
  }

  save(): void {
    // Defense in depth -- the template already disables the Save button for
    // this case, but a save triggered any other way must not silently write
    // onto a different company (see `isLegalEntity`'s own doc comment).
    if (this.isLegalEntity) {
      this.messageService.add({ severity: 'error', summary: 'Не можна редагувати', detail: 'Рівень встановлюється на головній компанії — відкрийте її форму' });
      return;
    }
    if (!this.profile?.tier) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Рівень (tier) обов’язковий' });
      return;
    }

    this.saving = true;
    this.saveQuery =
      this.agentAdminApi.saveCustomerTier(this.cliId, this.profile).subscribe({
        next: () => {
          this.saving = false;
          this.messageService.add({ severity: 'success', summary: 'Збережено', detail: 'Рівень клієнта оновлено' });
          this.loadProfile();
        },
        error: err => {
          this.saving = false;
          console.error('*** EditAgentCustomerTierTabComponent save: Error: ' + err);
          this.messageService.add({ severity: 'error', summary: 'Не вдалося зберегти', detail: err?.error?.message || err?.message || 'Unknown error' });
        }
      });
  }
}
